import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationField } from '../../types/translationTypes';
import { SUPPORTED_DOCUMENT_LANGUAGES } from '../invoice/DocumentLanguageSelector';

interface TranslationReviewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  originalLanguage: string;
  targetLanguage: string;
  fields: TranslationField[];
  onFieldChange: (key: string, text: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function getLangNativeName(code: string): string {
  return SUPPORTED_DOCUMENT_LANGUAGES.find(l => l.code === code)?.nativeName ?? code.toUpperCase();
}

// Group fields into sections for display
function groupFields(fields: TranslationField[]) {
  const items: TranslationField[] = [];
  const docFields: TranslationField[] = [];

  for (const f of fields) {
    if (f.fieldType === 'item_name' || f.fieldType === 'item_description') {
      items.push(f);
    } else {
      docFields.push(f);
    }
  }
  return { items, docFields };
}

const TranslationReviewModal: React.FC<TranslationReviewModalProps> = ({
  isOpen,
  isLoading,
  originalLanguage,
  targetLanguage,
  fields,
  onFieldChange,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('common');

  if (!isOpen) return null;

  const origName = getLangNativeName(originalLanguage);
  const targetName = getLangNativeName(targetLanguage);
  const { items: itemFields, docFields } = groupFields(fields);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('translationReview.title', 'Übersetzungsvorschau')}
            <span className="ml-2 text-faint font-normal text-base">
              {origName} → {targetName}
            </span>
          </h2>
          <button
            onClick={onCancel}
            className="text-muted hover:text-gray-600 transition-colors"
            aria-label="Schließen"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              <p className="text-faint text-sm">
                {t('translationReview.loading', 'Übersetze Texte...')}
              </p>
            </div>
          ) : fields.length === 0 ? (
            <p className="text-faint text-sm py-8 text-center">
              Keine übersetzbaren Felder gefunden.
            </p>
          ) : (
            <div className="space-y-6">
              {/* Column headers */}
              <div className="grid grid-cols-2 gap-4 sticky top-0 bg-white py-1">
                <div className="text-xs font-semibold text-faint uppercase tracking-wide px-2">
                  {t('translationReview.originalColumn', 'Original ({{lang}})', { lang: origName })}
                </div>
                <div className="text-xs font-semibold text-faint uppercase tracking-wide px-2">
                  {t('translationReview.translationColumn', 'Übersetzung ({{lang}})', { lang: targetName })}
                </div>
              </div>

              {/* Items section */}
              {itemFields.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b">
                    {t('translationReview.sections.items', 'Artikel')}
                  </div>
                  <div className="space-y-3">
                    {itemFields.map(field => (
                      <FieldRow
                        key={field.key}
                        field={field}
                        onFieldChange={onFieldChange}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Document-level fields */}
              {docFields.map(field => (
                <div key={field.key}>
                  <div className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b">
                    {getSectionLabel(field.fieldType, t)}
                  </div>
                  <FieldRow field={field} onFieldChange={onFieldChange} t={t} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            {t('translationReview.cancel', 'Abbrechen')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading || fields.length === 0}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {t('translationReview.confirm', 'Bestätigen')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

function getSectionLabel(fieldType: string, t: Function): string {
  switch (fieldType) {
    case 'paymentTerms': return t('translationReview.sections.paymentTerms', 'Zahlungsbedingungen');
    case 'notes': return t('translationReview.sections.notes', 'Notizen');
    case 'offerText': return t('translationReview.sections.offerText', 'Angebotstext');
    case 'projectName': return t('translationReview.sections.projectName', 'Projektname');
    default: return fieldType;
  }
}

interface FieldRowProps {
  field: TranslationField;
  onFieldChange: (key: string, text: string) => void;
  t: Function;
}

const FieldRow: React.FC<FieldRowProps> = ({ field, onFieldChange, t }) => {
  const isMultiline = field.originalText.length > 80 || field.originalText.includes('\n');

  return (
    <div className="grid grid-cols-2 gap-4 items-stretch">
      {/* Original (read-only) */}
      <div className="flex flex-col">
        <div className="text-xs text-muted mb-1">{field.label}</div>
        <div className="flex-1 bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap min-h-[2.5rem]">
          {field.originalText}
        </div>
      </div>

      {/* Editable translation */}
      <div className="flex flex-col">
        <div className="text-xs text-muted mb-1 flex items-center gap-1">
          {field.label}
          {field.isFromCache && (
            <span
              className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full"
              title={t('translationReview.cachedHint', 'Aus Cache (zuvor gespeichert)')}
            >
              ✓
            </span>
          )}
        </div>
        {isMultiline ? (
          <textarea
            value={field.translatedText}
            onChange={e => onFieldChange(field.key, e.target.value)}
            className="flex-1 w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[5rem]"
          />
        ) : (
          <input
            type="text"
            value={field.translatedText}
            onChange={e => onFieldChange(field.key, e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
      </div>
    </div>
  );
};

export default TranslationReviewModal;
